import React from 'react';
import { useTheme } from '@mui/material';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { DonutChart } from '../../../../../shared/components/charts';
import { AnnotatableChart } from '../../../../../shared/components/charts/AnnotatableChart';
import { WellnessScoreGauge } from './WellnessScoreGauge';
import { roundToDecimal } from '../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import type { DistributionEntry, SleepTrendPoint, SleepTrendMetric } from '../api/types';

interface SleepGaugeProps {
  isLoading: boolean;
  weatherFilters: MetricsFilters;
  showBreakdown?: boolean;
  breakdownOpen?: boolean;
  onToggleBreakdown?: () => void;
}

export const SleepGauge: React.FC<SleepGaugeProps> = ({
  isLoading,
  weatherFilters,
  showBreakdown,
  breakdownOpen,
  onToggleBreakdown,
}) => {
  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  return (
    <WellnessScoreGauge
      metric='sleep'
      filterParams={weatherFilters}
      showBreakdown={showBreakdown}
      breakdownOpen={breakdownOpen}
      onToggleBreakdown={onToggleBreakdown}
    />
  );
};

interface SleepStagesProps {
  sleepStages: DistributionEntry[] | undefined;
  isLoading: boolean;
}

export const SleepStages: React.FC<SleepStagesProps> = ({ sleepStages, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  const getSleepStageColor = (name: string) => {
    const n = name.toLowerCase();
    if (n === 'deep') return theme.palette.success.main;
    if (n === 'rem') return theme.palette.warning.main;
    if (n === 'light') return theme.palette.info.main;
    return theme.palette.error.main;
  };

  const stagesData = (sleepStages ?? []).map((d) => ({
    label: d.name,
    value: d.value,
    color: getSleepStageColor(d.name),
  }));

  return (
    <DashboardCard
      title='Sleep Stage Breakdown'
      subtitle='Distribution of time spent in awake, light, REM, and deep sleep phases.'
      expandable
      expandedContent={<DonutChart data={stagesData} height={350} />}
    >
      <DonutChart data={stagesData} height={220} />
    </DashboardCard>
  );
};

interface SleepTrendProps {
  deptName: string;
  trends: SleepTrendPoint[] | undefined;
  clientId: string | null;
  weatherFilters: MetricsFilters;
  selectedMetric: SleepTrendMetric;
  canEditEvents?: boolean;
}

const METRIC_CONFIG: Record<
  SleepTrendMetric,
  {
    series: Array<{ dataKey: string; label: string; color: string; area: boolean }>;
    yAxisMax?: number;
  }
> = {
  sleep_stages: {
    series: [
      { dataKey: 'sleep_stage_deep', label: 'Deep', color: '#4caf50', area: true },
      { dataKey: 'sleep_stage_rem', label: 'REM', color: '#ff9800', area: true },
      { dataKey: 'sleep_stage_light', label: 'Light', color: '#2196f3', area: true },
      { dataKey: 'sleep_stage_awake', label: 'Awake', color: '#e91e63', area: true },
    ],
  },
  total_sleep: {
    series: [{ dataKey: 'hours_of_sleep', label: 'Hours of Sleep', color: '#9c27b0', area: true }],
  },
  efficiency: {
    series: [{ dataKey: 'sleep_efficiency', label: 'Sleep Efficiency', color: '#3f51b5', area: true }],
    yAxisMax: 100,
  },
  sleep_debt: {
    series: [{ dataKey: 'sleep_debt', label: 'Sleep Debt', color: '#f44336', area: true }],
  },
};

export const SleepTrend: React.FC<SleepTrendProps> = ({
  deptName,
  trends,
  clientId,
  weatherFilters,
  selectedMetric,
  canEditEvents = false,
}) => {
  const trendData =
    trends?.map((t) => ({
      label: t.period_start,
      sleep_efficiency: t.sleep_efficiency != null ? roundToDecimal(t.sleep_efficiency) : null,
      sleep_debt: t.sleep_debt != null ? roundToDecimal(t.sleep_debt) : null,
      hours_of_sleep: t.hours_of_sleep != null ? roundToDecimal(t.hours_of_sleep) : null,
      sleep_stage_awake: t.sleep_stage_awake != null ? roundToDecimal(t.sleep_stage_awake) : null,
      sleep_stage_light: t.sleep_stage_light != null ? roundToDecimal(t.sleep_stage_light) : null,
      sleep_stage_rem: t.sleep_stage_rem != null ? roundToDecimal(t.sleep_stage_rem) : null,
      sleep_stage_deep: t.sleep_stage_deep != null ? roundToDecimal(t.sleep_stage_deep) : null,
    })) ?? [];

  const config = METRIC_CONFIG[selectedMetric];

  return (
    <DashboardCard
      title='Sleep Trends'
      subtitle={`${deptName} sleep metrics over time`}
      expandable
      expandedContent={
        <AnnotatableChart
          clientId={clientId}
          startDate={weatherFilters.start_date}
          endDate={weatherFilters.end_date}
          weatherFilters={weatherFilters}
          canEdit={canEditEvents}
          data={trendData}
          xAxisKey='label'
          series={config.series}
          yAxisMin={0}
          yAxisMax={config.yAxisMax}
          height={500}
        />
      }
    >
      <AnnotatableChart
        clientId={clientId}
        startDate={weatherFilters.start_date}
        endDate={weatherFilters.end_date}
        weatherFilters={weatherFilters}
        canEdit={canEditEvents}
        data={trendData}
        xAxisKey='label'
        series={config.series}
        yAxisMin={0}
        yAxisMax={config.yAxisMax}
        height={250}
      />
    </DashboardCard>
  );
};
